import React, { useMemo, useState } from 'react';
import { confirmable, createConfirmation } from 'react-confirm';
import Pluralize from 'pluralize';

import BasicLabel from 'components/BasicLabel';
import CheckboxOrRadioGroup from 'components/CheckboxOrRadioGroup';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicButton from 'components/BasicButton';
import Popup from 'components/Popup';
import BasicCheckbox from 'components/BasicCheckbox';
import Dialog from 'scenes/Dialog/index';

import UxHelpers from 'helpers/ux-helpers';

const styles = {
  textAlign: 'left',
  fontWeight: '600',
  marginBottom: '10px',
  maxWidth: '560px',
};

const MassSendJobDialog = props => {
  const {
    show,
    proceed,
    dismiss,
    cancel,
    hasCloseButton,
    popupStyles,
    params: { type, diff, clear, clearNotifyOff, dirty, dirtyNotifyOff },
  } = props;

  const [selectedOptions, setSelectedOption] = useState({});
  const [
    ignoreOffProcessNotification,
    setIgnoreOffProcessNotification,
  ] = useState('No');

  const counters = useMemo(() => {
    const [_clear, _dirty] =
      ignoreOffProcessNotification === 'No'
        ? [
            Number(clear) - Number(clearNotifyOff),
            Number(dirty) - Number(dirtyNotifyOff),
          ]
        : [Number(clear), Number(dirty)];

    let visible = 0;
    visible += !!selectedOptions.includeClear ? _clear : 0;
    visible += !!selectedOptions.includeDirty ? _dirty : 0;

    const hidden = Number(clearNotifyOff) + Number(dirtyNotifyOff);

    return { hidden, visible, clear: _clear, dirty: _dirty };
  }, [
    selectedOptions,
    ignoreOffProcessNotification,
    diff,
    clear,
    clearNotifyOff,
    dirty,
    dirtyNotifyOff,
  ]);

  const proceedHandler = () => {
    proceed({ ...selectedOptions, ignoreOffProcessNotification });
    UxHelpers.overflowLock('unlock');
  };

  const dismissHandler = () => {
    dismiss();
    UxHelpers.overflowLock('unlock');
  };

  const onSelectChange = e => {
    const value = e.target.value;
    setSelectedOption(options => {
      const nextOptions = { ...options };
      nextOptions.hasOwnProperty(value)
        ? delete nextOptions[value]
        : (nextOptions[value] = 1);

      return nextOptions;
    });
  };

  const onIgnoreOffProcessNotificationChange = e => {
    setSelectedOption({});
    setIgnoreOffProcessNotification(e.target.value);
  };

  const contentRender = useMemo(() => {
    if (counters.clear > 0 && counters.dirty > 0) {
      return (
        <>
          {' '}
          <div style={styles}>Send to candidates who:</div>
          <div
            style={{
              margin: '10px 0',
              display: 'flex',
              justifyContent: 'left',
            }}
          >
            <BasicCheckbox
              value="includeClear"
              checked={!!selectedOptions.includeClear}
              singleRow={true}
              title={`haven't received Setup Email for this ${type} yet (${
                counters.clear
              } ${Pluralize('candidate', counters.clear)})`}
              name="includeClear"
              controlFunc={onSelectChange}
            />
          </div>
          <div
            style={{
              margin: '10px 0',
              display: 'flex',
              justifyContent: 'left',
            }}
          >
            <BasicCheckbox
              value="includeDirty"
              checked={!!selectedOptions.includeDirty}
              singleRow={true}
              title={`received Setup Email but haven't responded (${
                counters.dirty
              } ${Pluralize('candidate', counters.dirty)})`}
              name="includeDirty"
              controlFunc={onSelectChange}
            />
          </div>
          <div style={styles}>{`Total Setup Emails: ${counters.visible}`}</div>
        </>
      );
    }

    if (counters.clear > 0) {
      return (
        <div style={styles}>
          {`Setup Email will be sent to ${counters.clear} ${Pluralize(
            'candidate',
            counters.clear
          )} who never received it for this ${type} before.`}
        </div>
      );
    }

    if (counters.dirty > 0) {
      return (
        <div style={styles}>
          {`Setup Email will be sent to ${counters.dirty} ${Pluralize(
            'candidate',
            counters.dirty
          )} who ${Pluralize(
            'has',
            counters.dirty
          )} already received Setup Email for this ${type} but didn\`t respond.`}
        </div>
      );
    }

    return null;
  }, [ignoreOffProcessNotification, selectedOptions, counters]);

  const body = (
    <Dialog>
      {contentRender}
      {Number(diff) > 0 && (
        <div style={{ ...styles, maxWidth: '560px' }}>
          {`Please pay attention that there ${Pluralize(
            'is',
            Number(diff)
          )} ${diff} ${Pluralize('candidate', Number(diff))} who will
        not receive Setup Email for this ${type} as ${Pluralize(
            'he',
            Number(diff)
          )} ${Pluralize('has', Number(diff))} already received
        and responded to it or ${Pluralize('he', Number(diff))} ${Pluralize(
            'is',
            Number(diff)
          )} ${Pluralize('Recruiter', Number(diff))}`}
        </div>
      )}

      {counters.hidden > 0 && (
        <>
          {ignoreOffProcessNotification === 'No' && (
            <div style={{ marginBottom: '10px', maxWidth: '560px' }}>
              {`${counters.hidden} ${Pluralize(
                'user',
                counters.hidden
              )} ${Pluralize(
                'has',
                counters.hidden
              )} been excluded from the mailing, since ${
                type === 'process' ? 'Job' : 'Project'
              } notifications have been turned off.`}
            </div>
          )}

          <div
            className="form__group form__group--compact"
            style={{ marginBottom: 0 }}
          >
            <div className="form__row form__row--compact">
              <div className="form__column">
                <BasicLabel
                  name="IgnoreOffJobNotification"
                  text={`Send Job to candidates with email preferences of turn off ${
                    type === 'process' ? 'Job' : 'Project'
                  } notifications?`}
                />
              </div>
            </div>
          </div>

          <div
            className="form__group form__group--compact"
            style={{ marginBottom: '15px' }}
          >
            <div className="form__row form__row--compact">
              <div className="form__column">
                <CheckboxOrRadioGroup
                  name="IgnoreOffJobNotification"
                  type="radio"
                  options={['Yes', 'No']}
                  selectedOptions={ignoreOffProcessNotification}
                  directionRow={true}
                  controlFunc={onIgnoreOffProcessNotificationChange}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );

  const buttons = (
    <BasicButtonGroup
      groupStyles={{
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <BasicButton
        disabled={
          (counters.clear === 0 && counters.dirty === 0) ||
          (counters.clear > 0 &&
            counters.dirty > 0 &&
            !Object.keys(selectedOptions).length)
        }
        controlFunc={proceedHandler}
        btnWidth={200}
        btnHeight={40}
        btnFontWeight={500}
        btnArrow={false}
        btnTextColor="#27282d"
        btnText="Send"
      />
      <BasicButton
        controlFunc={dismissHandler}
        btnWidth={200}
        btnHeight={40}
        btnFontWeight={500}
        btnArrow={false}
        btnTextColor="#27282d"
        btnText="Cancel"
      />
    </BasicButtonGroup>
  );

  return (
    show && (
      <Popup
        actions={{
          dismiss,
          cancel,
        }}
        buttonsRender={buttons}
        popupStyles={popupStyles}
        bodyRender={body}
        dismissWhenClose={true}
        hasCloseButton={hasCloseButton}
      />
    )
  );
};

MassSendJobDialog.propTypes = {};

export default createConfirmation(confirmable(MassSendJobDialog));
